import svgQuote from '@img/icons/background/quote.svg?url';
import svgChevron from '@img/icons/interface/chevron-down.svg?url';
import { css } from 'styled-components';
import { transition } from './animate';
import brand from './brand';
import { fluid } from './fluid';
import fonts from './fonts';
import { Device, from } from './media';
import scrollbars from './scrollbars';
export const headingStyles = css `
  ${fonts.intro};

  color: currentColor;
  margin: 0 0 ${fluid(16, 34)};
`;
export const h1Sizes = css `
  font-size: ${fluid(32, 64)};
  line-height: ${fluid(38, 74)};
`;
export const h1 = css `
  ${headingStyles};
  ${h1Sizes};
`;
export const h2 = css `
  ${headingStyles};

  font-size: ${fluid(28, 48)};
  line-height: ${fluid(36, 56)};
`;
export const h3 = css `
  ${headingStyles};

  font-size: ${fluid(24, 34)};
  line-height: ${fluid(32, 40)};
`;
export const h4 = css `
  ${headingStyles};

  font-size: ${fluid(20, 24)};
  line-height: ${fluid(26, 28)};
`;
export const h5 = css `
  ${headingStyles};

  font-size: ${fluid(18, 23)};
  line-height: ${fluid(24, 27)};
`;
export const h6 = css `
  ${headingStyles};

  font-size: ${fluid(16, 22)};
  line-height: ${fluid(20, 30)};
`;
const paragraphStyles = css `
  ${fonts.lato.regular};

  color: currentColor;
  margin: 0 0 ${fluid(16, 34)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};

  font-size: ${fluid(18, 20)};
  line-height: ${fluid(26, 28)};
`;
export const paragraphRegular = css `
  ${paragraphStyles};

  font-size: ${fluid(16, 18)};
  line-height: ${fluid(24, 28)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};

  font-size: ${fluid(14, 16)};
  line-height: ${fluid(22, 24)};
`;
export const paragraphExtraSmall = css `
  ${paragraphStyles};

  font-size: 14px;
  line-height: ${fluid(22, 24)};
`;
export const subheading = css `
  ${fonts.intro};

  font-size: ${fluid(12, 16)};
  line-height: ${fluid(14, 24)};
  margin-bottom: 16px;

  strong {
    font-family: inherit;
    color: currentColor;
    font-size: inherit;
    line-height: inherit;
    font-style: inherit;
  }

  @media ${from(Device.TabletLarge)} {
    margin-bottom: 12px;
  }
`;
export const link = css `
  ${transition('text-decoration-color')};

  display: inline-block;
  color: var(--textLinkColour);
  text-decoration: underline;
  text-decoration-color: var(--textDecorationColour, transparent);
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;

  &:not(span) {
    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: auto;
      outline-offset: 2px;
      outline-width: 1px;
      outline-style: solid;
    }

    &:focus,
    &:hover {
      text-decoration-color: var(--textDecorationHoverColour, currentColor);
    }
  }

  /* Safari only link hover styles */
  @supports (hanging-punctuation: first) and (font: -apple-system-body) and
    (-webkit-appearance: none) {
    text-decoration: none;

    &:not(span) {
      &:focus,
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;
export const reverseLinkUnderline = css `
  --textDecorationColour: currentColor;
  --textDecorationHoverColour: transparent;

  /* Safari only link hover styles */
  @supports (hanging-punctuation: first) and (font: -apple-system-body) and
    (-webkit-appearance: none) {
    text-decoration: underline !important;

    &:not(span) {
      &:focus,
      &:hover {
        text-decoration: none !important;
      }
    }
  }
`;
export const blockquote = css `
  ${paragraphLarge};

  font-weight: 600;
  font-style: italic;
  color: currentColor;
  text-align: left;

  &::before {
    content: '';
    display: block;
    width: ${fluid(48, 64)};
    aspect-ratio: 1;
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: top left;
    background-image: url(${svgQuote});
    margin-bottom: ${fluid(16, 34)};
  }

  p {
    color: currentColor;
    font-size: inherit;
    line-height: inherit;
    font-style: inherit;
    font-family: inherit;
    margin: 0 0 ${fluid(16, 34)};

    &:last-child {
      margin: 0;
    }
  }

  footer {
    color: ${brand.primary.purple.dark};
    font-weight: 700;
    margin-top: ${fluid(32, 34)};

    cite {
      ${paragraphRegular};

      display: block;
      margin: 0;
    }
  }
`;
export const table = css `
  ${paragraphRegular};

  --borderColour: ${brand.primary.grey};
  --borderRadius: ${fluid(10, 20)};
  --headerBgColour: ${brand.primary.purple.light};
  --headerTextColour: ${brand.global.white};
  --paddingX: ${fluid(16, 26)};

  background: ${brand.global.white};
  border-collapse: collapse;
  color: currentColor;
  margin-bottom: ${fluid(16, 34)};
  width: 100% !important;

  td,
  th {
    border-right: 1px solid var(--headerTextColour);
    padding: 12px var(--paddingX);
    text-align: left;
  }

  thead {
    tr {
      background: var(--headerBgColour);

      td,
      th {
        ${fonts.intro};

        color: ${brand.global.white};
        padding: var(--paddingX);
        border-right: 1px solid var(--headerTextColour);

        &:first-child {
          border-top-left-radius: var(--borderRadius);
        }

        &:last-child {
          border-top-right-radius: var(--borderRadius);
        }
      }
    }
  }

  tbody {
    tr {
      &:nth-child(odd) {
        background-color: ${brand.global.white};
      }

      &:nth-child(even) {
        background-color: ${brand.primary.grey};
      }

      &:last-child td {
        border-bottom: 1px solid var(--borderColour);
      }

      td {
        &:first-child {
          border-left: 1px solid var(--borderColour);
        }

        &:last-child {
          border-right: 1px solid ${brand.primary.grey};
        }
      }
    }
  }
`;
export const contentStyles = css `
  h1,
  .h1-styles {
    ${h2};
  }

  h2,
  .h2-styles {
    ${h3};
  }

  h3,
  .h3-styles {
    ${h4};
  }

  h4,
  .h4-styles {
    ${h5}
  }

  h5,
  .h5-styles {
    ${h6}
  }

  h6,
  .h6-styles {
    ${headingStyles};

    font-size: ${fluid(15, 20)};
    line-height: ${fluid(19, 28)};
  }

  p {
    ${paragraphRegular}

    a {
      ${link};
    }
  }

  strong {
    ${fonts.lato.bold};
  }

  .large {
    ${paragraphLarge}
  }

  .small {
    ${paragraphSmall}
  }

  .x-small {
    ${paragraphExtraSmall}
  }

  .disclaimer {
    ${paragraphStyles};

    font-size: 12px;
    line-height: 16px;
  }

  .subheading {
    ${subheading};
  }

  /* Brand Colour Classes */
  .brand-purple-light {
    color: ${brand.primary.purple.light};
  }

  .brand-purple-dark {
    color: ${brand.primary.purple.dark};
  }

  .brand-blue-light {
    color: ${brand.primary.blue.light};
  }

  .brand-blue-dark {
    color: ${brand.primary.blue.dark};
  }

  .brand-green-light {
    color: ${brand.secondary.green.light};
  }

  .brand-green-mid {
    color: ${brand.secondary.green.mid};
  }

  .brand-green-dark {
    color: ${brand.secondary.green.dark};
  }

  .brand-orange-light {
    color: ${brand.secondary.orange.light};
  }

  .brand-orange-mid {
    color: ${brand.secondary.orange.mid};
  }

  .brand-orange-dark {
    color: ${brand.secondary.orange.dark};
  }

  .brand-pink-light {
    color: ${brand.secondary.pink.light};
  }

  .brand-pink-mid {
    color: ${brand.secondary.pink.mid};
  }

  .brand-pink-dark {
    color: ${brand.secondary.pink.dark};
  }

  .brand-yellow-light {
    color: ${brand.secondary.yellow.light};
  }

  .brand-yellow-mid {
    color: ${brand.secondary.yellow.mid};
  }

  .brand-yellow-dark {
    color: ${brand.secondary.yellow.dark};
  }

  a {
    ${link}
  }

  ol,
  ul {
    ${paragraphRegular};

    list-style: none;
    padding-left: 1.2em;
    margin: ${fluid(8, 16)} 0 ${fluid(16, 34)};

    li {
      color: currentColor;
      margin: 0 0 ${fluid(8, 16)};

      span {
        display: inline;
      }

      &::before {
        color: ${brand.primary.purple.light};
        display: inline-block;
        width: 1.2em;
        margin-left: -1.2em;
      }
    }
  }

  ol {
    counter-reset: li;

    li {
      counter-increment: li;

      &::before {
        content: counter(li) '.';
        width: 2.5ch; // Allow space for double digit numbers
        margin-left: -2.5ch;
      }
    }
  }

  // Ensure that inline styling hasn't been applied
  ul:not([style*='list-style-type']) li::before {
    content: '\\2022';
    font-weight: bold;
  }

  blockquote {
    ${blockquote};
  }

  hr {
    display: block;
    background: ${brand.primary.purple.light};
    border: 0;
    height: 2px;
    margin: ${fluid(16, 34)} 0;
    padding: 0;
    width: 100%;
  }

  table {
    ${table};
  }

  .responsive-table {
    ${scrollbars};

    position: relative;
    overflow-x: auto;
  }

  /* Details/Summary Accordion styles */
  details {
    display: block;
    color: ${brand.primary.blue.dark};
    padding: ${fluid(16, 18)};
    border-radius: ${fluid(10, 20)};
    background-color: ${brand.primary.grey};
    margin-bottom: ${fluid(16, 18)};

    summary {
      ${fonts.lato.semiBold};

      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      font-size: ${fluid(18, 24)};
      line-height: ${fluid(24, 28)};
      text-align: left;
      width: 100%;
      color: currentColor;
      cursor: pointer;
      margin-bottom: 2px;

      &::after {
        ${transition('transform')};

        content: '';
        display: block;
        width: 40px;
        height: 32px;
        background: currentColor;
        -webkit-mask-image: url(${svgChevron});
        mask-image: url(${svgChevron});
        mask-repeat: no-repeat;
        mask-size: cover;
        mask-position: center;
      }
    }

    &[open] summary::after {
      transform: rotate(180deg);
    }

    > div {
      padding: ${fluid(14, 20)} 0 6px;

      p,
      ul,
      ol {
        margin-bottom: ${fluid(12, 18)};
      }

      ul,
      ol {
        margin-left: ${fluid(4, 8)};

        li {
          margin-bottom: ${fluid(6, 10)};
        }
      }

      > *:first-child {
        margin-top: 0;
      }

      > *:last-child {
        margin-bottom: 0;
      }
    }
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
